/* Printable documents (prototype DOCS + WPS summary, settlement statement and salary cheque). */
import type { Role } from "../../data/types";
import type { MarginKey, PaperFam } from "./papers";

export type DocKey =
  | "payslip" | "register" | "gosi" | "wps" | "letter" | "directory" | "attendance" | "leaveform"
  | "voucher" | "settlement" | "cheque" | "idcard" | "label" | "envelope" | "test";

export interface DocDefaults { paper?: string; orient?: "portrait" | "landscape"; margin?: MarginKey; size?: "small" | "normal" | "large" }
export interface DocDef {
  key: DocKey;
  en: string;
  ar: string;
  icon: string;
  fams: PaperFam[];
  defaults: DocDefaults;
  roles: Role[];
  ref: string;
}

const T: Role[] = ["owner", "hr", "accountant"];
const OH: Role[] = ["owner", "hr"];
const ALL: Role[] = ["owner", "hr", "accountant", "employee"];

export const DOCS: DocDef[] = [
  { key: "payslip", en: "Payslip", ar: "قسيمة الراتب", icon: "receipt", fams: ["page", "roll"], defaults: { paper: "A4" }, roles: ALL, ref: "PS" },
  { key: "register", en: "Payroll register", ar: "سجل الرواتب", icon: "wallet", fams: ["page"], defaults: { paper: "A4", orient: "landscape", size: "small" }, roles: T, ref: "PR" },
  { key: "gosi", en: "GOSI contribution report", ar: "تقرير اشتراكات التأمينات", icon: "shield", fams: ["page"], defaults: { paper: "A4", orient: "landscape" }, roles: T, ref: "GOSI" },
  { key: "wps", en: "WPS / Mudad salary summary", ar: "ملخص ملف حماية الأجور (مدد)", icon: "banknote", fams: ["page"], defaults: { paper: "A4", orient: "landscape", size: "small" }, roles: T, ref: "WPS" },
  { key: "letter", en: "Letter / certificate", ar: "خطاب / شهادة", icon: "mail", fams: ["page"], defaults: { paper: "A4" }, roles: ["owner", "hr", "employee"], ref: "LTR" },
  { key: "directory", en: "Employee directory", ar: "دليل الموظفين", icon: "users", fams: ["page"], defaults: { paper: "A4" }, roles: T, ref: "DIR" },
  { key: "attendance", en: "Monthly attendance sheet", ar: "كشف الحضور الشهري", icon: "clock", fams: ["page"], defaults: { paper: "A4", orient: "landscape", size: "small" }, roles: OH, ref: "ATT" },
  { key: "leaveform", en: "Leave request form", ar: "نموذج طلب إجازة", icon: "plane", fams: ["page"], defaults: { paper: "A4" }, roles: ["owner", "hr", "employee"], ref: "LVF" },
  { key: "voucher", en: "Salary advance voucher", ar: "سند سلفة راتب", icon: "banknote", fams: ["page", "roll"], defaults: { paper: "A5" }, roles: T, ref: "ADV" },
  { key: "settlement", en: "End-of-service settlement", ar: "مخالصة نهاية الخدمة", icon: "log-out", fams: ["page"], defaults: { paper: "A4" }, roles: OH, ref: "EOS" },
  { key: "cheque", en: "Salary cheque", ar: "شيك راتب", icon: "credit", fams: ["page"], defaults: { paper: "CHQ", orient: "portrait", margin: "narrow" }, roles: T, ref: "CHQ" },
  { key: "idcard", en: "Employee ID card", ar: "بطاقة الموظف", icon: "id-card", fams: ["card"], defaults: { paper: "CR80", margin: "none" }, roles: ALL, ref: "IDC" },
  { key: "label", en: "Employee file label", ar: "ملصق ملف الموظف", icon: "file", fams: ["label", "labelsheet"], defaults: { paper: "L105", margin: "none" }, roles: T, ref: "LBL" },
  { key: "envelope", en: "Envelope", ar: "ظرف", icon: "mail", fams: ["page"], defaults: { paper: "DL", orient: "landscape", margin: "normal" }, roles: T, ref: "ENV" },
  { key: "test", en: "Printer alignment test", ar: "صفحة اختبار المحاذاة", icon: "target", fams: ["page", "roll", "card", "label"], defaults: {}, roles: ALL, ref: "TST" },
];

export const docDef = (k: string): DocDef => DOCS.find((d) => d.key === k) ?? DOCS[0];
export const docsFor = (role: Role | null): DocDef[] => DOCS.filter((d) => !!role && d.roles.includes(role));
export const isDocKey = (k: string | null | undefined): k is DocKey => !!k && DOCS.some((d) => d.key === k);

/** Letter kinds: [key, en title, ar title]. */
export const LETTER_KINDS: [string, string, string][] = [
  ["Salary certificate", "Salary certificate", "تعريف بالراتب"],
  ["Experience letter", "Experience letter", "شهادة خبرة"],
  ["No objection certificate", "No objection certificate", "خطاب عدم ممانعة"],
  ["Salary transfer letter", "Salary transfer letter", "خطاب تحويل راتب"],
];

/** Documents with the "print for one / all employees" switch. */
export const SCOPED: DocKey[] = ["payslip", "label", "cheque"];
/** Documents that print for a single chosen employee. */
export const PER_EMP: DocKey[] = ["payslip", "letter", "voucher", "idcard", "label", "envelope", "leaveform", "settlement", "cheque"];
/** Documents with a pay period. */
export const PERIODIC: DocKey[] = ["payslip", "register", "cheque"];
